import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FileAudio, FileText, Film, Image as ImageIcon, Mic } from 'lucide-react';

const OPTIONS = [
  { id: 'audio', label: 'Audio', icon: FileAudio, hint: 'MP3, WAV, M4A, AAC' },
  { id: 'video', label: 'Video', icon: Film, hint: 'MP4, MOV, WebM' },
  { id: 'image', label: 'Image', icon: ImageIcon, hint: 'PNG, JPG, WEBP' },
  { id: 'file', label: 'File', icon: FileText, hint: 'PDF, DOCX, PPTX, TXT' },
  { id: 'record', label: 'Record', icon: Mic, hint: 'Voice instruction' },
];

/**
 * Attach menu only. File inputs live in the parent (always mounted) and are
 * activated via native <label htmlFor> — no programmatic input.click().
 */
export function AttachPopover({
  open,
  onClose,
  anchorRef,
  disabled,
  inputIds,
  onStartRecord,
}) {
  const listId = useId();
  const panelRef = useRef(null);
  const [focused, setFocused] = useState(0);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 280 });

  useEffect(() => {
    if (!open || !anchorRef?.current) return undefined;
    const place = () => {
      const el = anchorRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const width = Math.min(300, Math.max(260, window.innerWidth - 24));
      let left = rect.left;
      if (left + width > window.innerWidth - 12) left = window.innerWidth - width - 12;
      if (left < 12) left = 12;
      let top = rect.bottom + 8;
      const estHeight = 320;
      if (top + estHeight > window.innerHeight - 12) {
        top = Math.max(12, rect.top - estHeight - 8);
      }
      setCoords({ top, left, width });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, anchorRef]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose?.();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocused((i) => (i + 1) % OPTIONS.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocused((i) => (i - 1 + OPTIONS.length) % OPTIONS.length);
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        const opt = OPTIONS[focused];
        if (!opt || disabled) return;
        if (opt.id === 'record') {
          onClose?.();
          onStartRecord?.();
          return;
        }
        const input = document.getElementById(inputIds?.[opt.id]);
        input?.click();
      }
    };
    const onPointer = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      if (anchorRef?.current?.contains(e.target)) return;
      // File input is outside the panel (in parent) — don't treat as outside
      if (e.target?.closest?.('[data-attach-file-input]')) return;
      onClose?.();
    };
    document.addEventListener('keydown', onKey);
    const t = window.setTimeout(() => {
      document.addEventListener('mousedown', onPointer);
    }, 100);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [open, focused, onClose, anchorRef, disabled, onStartRecord, inputIds]);

  if (!open) return null;

  return createPortal(
    <div
      ref={panelRef}
      role="menu"
      id={listId}
      aria-label="Attach media"
      className="fixed z-[250] rounded-2xl border border-black/5 bg-white text-[#1a1a1a] shadow-[0_8px_28px_rgba(0,0,0,0.18),0_2px_8px_rgba(0,0,0,0.08)] overflow-hidden"
      style={{ top: coords.top, left: coords.left, width: coords.width }}
      data-testid="attach-popover"
    >
      <div className="px-4 pt-3.5 pb-2 border-b border-black/[0.06]">
        <p className="text-[13px] font-semibold tracking-tight">Attach</p>
        <p className="text-[11px] text-[#5f6368] mt-0.5">Upload to S3 · saved on your project</p>
      </div>
      <ul className="py-1.5" role="none">
        {OPTIONS.map((opt, idx) => {
          const Icon = opt.icon;
          const active = focused === idx;
          const inputId = inputIds?.[opt.id];

          if (opt.id === 'record') {
            return (
              <li key={opt.id} role="none">
                <button
                  type="button"
                  role="menuitem"
                  disabled={disabled}
                  tabIndex={active ? 0 : -1}
                  onMouseEnter={() => setFocused(idx)}
                  onClick={() => {
                    if (disabled) return;
                    onClose?.();
                    onStartRecord?.();
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors cursor-pointer disabled:opacity-50 ${
                    active ? 'bg-[#e8f0fe]' : 'hover:bg-[#f1f3f4]'
                  }`}
                >
                  <span
                    className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                      active ? 'bg-[#d2e3fc] text-[#1a73e8]' : 'bg-[#f1f3f4] text-[#3c4043]'
                    }`}
                  >
                    <Icon className="w-4 h-4" strokeWidth={2} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13px] font-medium text-[#202124]">{opt.label}</span>
                    <span className="block text-[11px] text-[#5f6368] truncate">{opt.hint}</span>
                  </span>
                </button>
              </li>
            );
          }

          return (
            <li key={opt.id} role="none">
              <label
                htmlFor={disabled ? undefined : inputId}
                role="menuitem"
                tabIndex={active ? 0 : -1}
                onMouseEnter={() => setFocused(idx)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (!disabled) document.getElementById(inputId)?.click();
                  }
                }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors cursor-pointer ${
                  disabled ? 'opacity-50 pointer-events-none' : ''
                } ${active ? 'bg-[#e8f0fe]' : 'hover:bg-[#f1f3f4]'}`}
                data-testid={`attach-option-${opt.id}`}
              >
                <span
                  className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                    active ? 'bg-[#d2e3fc] text-[#1a73e8]' : 'bg-[#f1f3f4] text-[#3c4043]'
                  }`}
                >
                  <Icon className="w-4 h-4" strokeWidth={2} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[13px] font-medium text-[#202124]">{opt.label}</span>
                  <span className="block text-[11px] text-[#5f6368] truncate">{opt.hint}</span>
                </span>
              </label>
            </li>
          );
        })}
      </ul>
    </div>,
    document.body
  );
}

export default AttachPopover;
